import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  TrendingUp as IncreaseIcon,
  TrendingDown as DecreaseIcon,
  SwapHoriz as TransferIcon,
  Build as AdjustmentIcon,
} from '@mui/icons-material'
// import { DatePicker } from '@mui/x-date-pickers/DatePicker'
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { InventoryService } from '../../services/inventoryService'
import { StockInfo, StockMovement } from '../../types/inventory'

interface StockMovementDialogProps {
  open: boolean
  stock: StockInfo | null
  onClose: () => void
}

const StockMovementDialog: React.FC<StockMovementDialogProps> = ({
  open,
  stock,
  onClose,
}) => {
  const [movements, setMovements] = useState<StockMovement[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) // 30 days ago
  )
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [limit, setLimit] = useState(50)

  useEffect(() => {
    if (stock && open) {
      loadMovements()
    }
  }, [stock, open, startDate, endDate, limit])

  const loadMovements = async () => {
    if (!stock) return

    setIsLoading(true)
    try {
      const movementData = await InventoryService.getStockMovements(
        stock.variant_id,
        startDate?.toISOString(),
        endDate?.toISOString(),
        limit
      )
      setMovements(movementData)
    } catch (error) {
      console.error('Failed to load stock movements:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getMovementIcon = (movementType: string, quantity: number) => {
    if (quantity > 0) {
      return <IncreaseIcon color="success" />
    } else if (quantity < 0) {
      return <DecreaseIcon color="error" />
    } else {
      return <AdjustmentIcon color="primary" />
    }
  }

  const getMovementTypeLabel = (movementType: string) => {
    const typeLabels: Record<string, string> = {
      manual: 'Manual Adjustment',
      sale: 'Sale',
      purchase: 'Purchase',
      return: 'Return',
      damage: 'Damage/Loss',
      correction: 'Stock Correction',
      transfer: 'Transfer',
      reservation: 'Reservation',
      fulfillment: 'Order Fulfillment',
    }
    return typeLabels[movementType] || movementType
  }

  const getMovementColor = (movementType: string, quantity: number) => {
    if (quantity > 0) return 'success'
    if (quantity < 0) return 'error'
    return 'default'
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Stock Movement History</Typography>
          {stock && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {stock.product_name} - {stock.variant_name} (SKU: {stock.variant_sku})
            </Typography>
          )}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            {/* Current Stock Info */}
            {stock && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Current Stock
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {stock.current_stock.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Reserved
                    </Typography>
                    <Typography variant="body2" color="warning.main">
                      {stock.reserved_stock.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Available
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      {stock.available_stock.toLocaleString()}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            {/* Filters */}
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <TextField
                label="Start Date"
                type="date"
                value={startDate?.toISOString().split('T')[0] || ''}
                onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                size="small"
                sx={{ minWidth: 150 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={endDate?.toISOString().split('T')[0] || ''}
                onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                size="small"
                sx={{ minWidth: 150 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Limit"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value))}
                size="small"
                sx={{ minWidth: 100 }}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </TextField>
              <Button onClick={loadMovements} variant="outlined" size="small">
                Refresh
              </Button>
            </Stack>

            {/* Movements List */}
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : movements.length === 0 ? (
              <Alert severity="info">
                No stock movements found for the selected period.
              </Alert>
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Movement History ({movements.length} records)
                </Typography>
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {movements.map((movement) => (
                    <ListItem key={movement.id} divider>
                      <ListItemIcon>
                        {getMovementIcon(movement.movement_type, movement.quantity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={600}>
                              {getMovementTypeLabel(movement.movement_type)}
                            </Typography>
                            <Chip
                              label={`${movement.quantity > 0 ? '+' : ''}${movement.quantity}`}
                              color={getMovementColor(movement.movement_type, movement.quantity)}
                              size="small"
                            />
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {movement.reason}
                            </Typography>
                            {movement.reference_id && (
                              <Typography variant="caption" color="text.secondary">
                                Reference: {movement.reference_id}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {new Date(movement.created_at).toLocaleString()} • by {movement.created_by}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
  )
}

export default StockMovementDialog